// VehicleProfileService.tsx
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { VehicleProfileService } from '../../core/services';
import { PageSize } from '../../type';
import { GetVehicleInspectionByIdResponse } from './interface';

export const useGetVehicleService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-vehicles-data'],
      queryFn: async ({ pageParam }) => {
        return VehicleProfileService.getVehicle({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          platenumber: initialParams.platenumber,
          vin: initialParams.vin,
          makeTitle: initialParams.makeTitle,
          modelTitle: initialParams.modelTitle,
          customerName: initialParams.customerName,
          customerPhone: initialParams.customerPhone,
          accountId: initialParams.accountId,
          userId: initialParams.userId,
          sort: initialParams.sort,
        });
      },
      enabled: false,
      initialPageParam: 1,
      getNextPageParam: (res) => {
        return !res?.last ? res?.number + 1 : undefined;
      },
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetVehicleDropdownService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-vehicles-dropdown-data'],
      queryFn: async ({ pageParam }) => {
        return VehicleProfileService.getVehiclesDropdown({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          search: initialParams.search,
        });
      },
      enabled: false,
      initialPageParam: 1,
      getNextPageParam: (res) => {
        return !res?.last ? res?.number + 1 : undefined;
      },
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetBookingService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-bookings-data'],
      queryFn: async ({ pageParam }) => {
        return VehicleProfileService.getBookings({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          vehicleId: initialParams.vehicleId,
          title: initialParams.title,
          status: initialParams.status,
          refNo: initialParams.refNo,
        });
      },
      enabled: false,
      initialPageParam: 1,
      getNextPageParam: (res) => {
        return !res?.last ? res?.number + 1 : undefined;
      },
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetVehicleInspectionService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-vehicle-inspections-data'],
      queryFn: async ({ pageParam }) => {
        return VehicleProfileService.getVehicleInspections({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          vehicleId: initialParams.vehicleId,
          title: initialParams.title,
          plateNumber: initialParams.plateNumber,
          customerName: initialParams.customerName,
          customerPhone: initialParams.customerPhone,
          start: initialParams.start,
          end: initialParams.end,
        });
      },
      enabled: false,
      initialPageParam: 1,
      getNextPageParam: (res) => {
        return !res?.last ? res?.number + 1 : undefined;
      },
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetVehicleInspectionByIDService = (id: number) => {
  const { data, isLoading, isRefetching, error, refetch } = useQuery<GetVehicleInspectionByIdResponse, unknown>({
    queryKey: ['get-vehicle-inspection-by-id-data', id],
    queryFn: async () => {
      return VehicleProfileService.getVehicleInspectionByID(id);
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    isRefetching,
    error,
    refetch,
  };
};

export const useGetVehicleRecommendationsService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-vehicle-service-recommendations-data'],
      queryFn: async ({ pageParam }) => {
        return VehicleProfileService.getVehicleServiceRecommendations({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          vehicleId: initialParams.vehicleId,
          type: initialParams.type,
          typeName: initialParams.typeName,
        });
      },
      enabled: false,
      initialPageParam: 1,
      getNextPageParam: (res) => {
        return !res?.last ? res?.number + 1 : undefined;
      },
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetVehicleHistoryService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-vehicle-service-history-data'],
      queryFn: async ({ pageParam }) => {
        return VehicleProfileService.getVehicleServiceHistory({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          vehicleId: initialParams.vehicleId,
          title: initialParams.title,
          serviceCode: initialParams.serviceCode,
        });
      },
      enabled: false,
      initialPageParam: 1,
      getNextPageParam: (res) => {
        return !res?.last ? res?.number + 1 : undefined;
      },
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};
